import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import type { useMutation } from '@apollo/client/react/compiled'
import type { MutationResultAdditionalProps, PartialMutationProviderOutput } from '@common/types'
import type { ConfirmButtonTransitionState } from '@components/ConfirmButton'
import { hueToPillColorDark, hueToPillColorLight } from '@components/Datagrid'
import type { ThemePalette } from '@components/Theme'
import type { AccountScopeCode, AuthScopeCode, CommonScopeCode, Scope } from '@gql'
import type { GraphQLFormattedError } from 'graphql/index'

export function getMutationState(
  called: boolean,
  loading: boolean,
  error: ErrorLike | undefined,
): ConfirmButtonTransitionState {
  if (loading) {
    return 'loading'
  }

  if (called) {
    return error && (ServerError.is(error) || !!CombinedGraphQLErrors.is(error))
      ? 'error'
      : 'success'
  }

  return 'default'
}

export function getMutationStatus(opts: useMutation.Result): ConfirmButtonTransitionState {
  return getMutationState(opts.called, opts.loading, opts.error)
}

type ErrorScopeCode = AuthScopeCode | AccountScopeCode | CommonScopeCode
export function apolloHasError(error: ErrorLike, scope: Scope, errorCode: ErrorScopeCode) {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some((i: GraphQLFormattedError) => isGraphQLErrorCode(i, scope, errorCode))
  } else {
    return false
  }
}

export function isGraphQLErrorCode(
  error: GraphQLFormattedError,
  scope: Scope,
  errorCode: ErrorScopeCode,
) {
  return error.extensions?.scope === scope && error.extensions?.scopeCode === errorCode
}

export function getMutationProviderData<
  TData extends Record<string, any>,
  TVariables extends Record<string, any>,
>(
  mutateFn: useMutation.MutationFunction<TData, TVariables>,
  opts: useMutation.Result<TData> & MutationResultAdditionalProps,
): PartialMutationProviderOutput<TData, TVariables> {
  return {
    mutate: (variables) => mutateFn({ variables }),
    opts,
  }
}

interface AnyEvent {
  stopPropagation: () => void
}
export function stopPropagation(cb: (event?: AnyEvent) => void) {
  return (event: AnyEvent) => {
    event.stopPropagation()
    cb(event)
  }
}

export type PillStatusType =
  'error' | 'warning' | 'info' | 'success' | 'neutral' | 'attention' | 'generic'

export const getStatusColor = ({
  status,
  currentPalette,
}: {
  status: PillStatusType
  currentPalette: ThemePalette
}) => {
  const statusHue = getStatusHue(status)

  return currentPalette.mode === 'dark'
    ? hueToPillColorDark(statusHue)
    : hueToPillColorLight(statusHue)
}

const getStatusHue = (status: PillStatusType): number => {
  const red = 8
  const blue = 240
  const green = 135
  const amber = 75
  const orange = 40
  const gray = 0

  switch (status) {
    case 'error':
      return red
    case 'info':
      return blue
    case 'success':
      return green
    case 'warning':
      return amber
    case 'neutral':
    case 'generic':
      return gray
    case 'attention':
      return orange
    default:
      return gray
  }
}
